import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { UserPlus, Copy, Trash2, Users, Mail } from "lucide-react";

const ROLE_LABELS = {
  buyer_agent: "Buyer's Agent",
  transaction_coordinator: "Transaction Coordinator",
};

export default function CollaboratorsCard({ listing, onListingUpdate }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("buyer_agent");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const active = (listing?.collaborators || []).filter((c) => !c.revoked_at);

  const invite = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/collaborators`, {
        name: name.trim(), email: email.trim(), role,
      });
      onListingUpdate(data);
      toast.success(`Invite sent to ${name.trim()}`);
      setOpen(false); setName(""); setEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not invite.");
    } finally { setBusy(false); }
  };

  const copyLink = async (c) => {
    const base = window.location.origin;
    const url = `${base}/transaction/${c.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Private link copied");
    } catch { toast.error("Could not copy"); }
  };

  const revoke = async () => {
    if (!revoking) return;
    try {
      const { data } = await api.delete(`/listings/${listing.id}/collaborators/${revoking.id}`);
      onListingUpdate(data);
      toast.success("Access revoked");
      setRevoking(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke.");
    }
  };

  return (
    <div className="rounded-md border border-stone-200 bg-white p-6 mb-6" data-testid="collaborators-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-stone-500">Transaction Hub access</p>
          <h3 className="font-heading text-2xl font-semibold text-stone-900 mt-1 flex items-center gap-2">
            <Users className="h-5 w-5 text-stone-400" />
            Buyer-side collaborators
          </h3>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed max-w-xl">
            Invite the buyer's agent and transaction coordinator. They'll see mutual escrow milestones + the ratified agreement only — no intake, lockbox codes, or pricing notes.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="rounded-full px-5 text-white"
          style={{ backgroundColor: "#010082" }}
          data-testid="invite-collaborator-button"
        >
          <UserPlus className="h-3.5 w-3.5 mr-1.5" />
          Invite collaborator
        </Button>
      </div>

      {active.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500 italic">No collaborators invited yet.</p>
      ) : (
        <ul className="mt-5 divide-y divide-stone-100" data-testid="collaborators-list">
          {active.map((c) => (
            <li key={c.id} className="py-3 flex items-start gap-4 flex-wrap" data-testid={`collaborator-${c.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-stone-900">{c.name}</span>
                  <span className="inline-flex items-center rounded-full border border-stone-300 bg-stone-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-stone-600 font-semibold">
                    {ROLE_LABELS[c.role] || c.role}
                  </span>
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> {c.email}
                </div>
                {c.last_viewed_at && (
                  <div className="text-[10px] text-stone-400 mt-0.5 uppercase tracking-widest font-semibold">
                    Last viewed {new Date(c.last_viewed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => copyLink(c)}
                  data-testid={`collaborator-copy-${c.id}`}
                  className="h-8 rounded-full border-stone-300 text-xs"
                >
                  <Copy className="h-3 w-3 mr-1.5" />
                  Copy link
                </Button>
                <button
                  type="button"
                  onClick={() => setRevoking(c)}
                  data-testid={`collaborator-revoke-${c.id}`}
                  className="text-rose-600 hover:text-rose-800"
                  title="Revoke access"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-collaborator-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Invite a collaborator</DialogTitle>
            <DialogDescription>
              They'll get a private link. Only Phase 7 escrow milestones + the ratified agreement are shared — intake, lockbox codes, and listing-side notes stay private.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">Role</Label>
              <div className="flex gap-2">
                {Object.entries(ROLE_LABELS).map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setRole(val)}
                    data-testid={`invite-role-${val}`}
                    aria-pressed={role === val}
                    className={`inline-flex items-center justify-center h-10 px-4 rounded-full text-xs font-semibold border transition-all ${
                      role === val ? "text-white border-transparent" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                    }`}
                    style={role === val ? { backgroundColor: "#010082" } : undefined}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" placeholder="Full name" className="h-10 border-stone-300" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" placeholder="name@brokerage.com" className="h-10 border-stone-300" />
            </div>
          </div>
          <DialogFooter className="mt-5">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-stone-300">Cancel</Button>
            <Button onClick={invite} disabled={busy} className="rounded-full text-white px-5" style={{ backgroundColor: "#010082" }} data-testid="invite-submit">
              {busy ? "Sending…" : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent data-testid="revoke-collaborator-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Revoke access?</AlertDialogTitle>
            <AlertDialogDescription>
              {revoking?.name} will immediately lose access to this transaction's hub. You can invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Nevermind</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} className="bg-rose-600 hover:bg-rose-700">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
